import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { studentService } from '../../services/studentService';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import AppIcon from '../../components/AppIcon';
import { useNavigate, useSearchParams } from 'react-router-dom';
import StudentLayout from '../../components/layouts/StudentLayout';
import * as enrollmentService from '../../services/enrollmentService';
import { admissionService } from '../../services/admissionService';
import { generateAdmissionLetter } from '../../utils/admissionLetter';
import { Download, FileText } from 'lucide-react';
import { supabase } from '../../lib/supabase';

// HOD resolutions stay in the database for audit, but must not be treated as
// a current student unit. This keeps the registration page in sync with the
// examination and historical-progress read paths.
const isVisibleUnitRegistration = registration => {
    const enrollmentStatus = String(registration?.enrollment_status || '').toLowerCase();
    const reviewStatus = String(registration?.review_status || '').toLowerCase();
    const resolutionType = String(registration?.resolution_type || '').toLowerCase();
    if (registration?.is_active === false) return false;
    if (['withdrawn', 'removed', 'rejected', 'cancelled', 'deleted'].includes(enrollmentStatus)) return false;
    if (['wrong_unit', 'already_completed', 'credit_transferred', 'elective_not_selected'].includes(reviewStatus)) return false;
    if (['wrong_unit', 'already_completed', 'credit_transfer', 'credit_transferred', 'elective_not_selected'].includes(resolutionType)) return false;
    return !enrollmentStatus || ['enrolled', 'active', 'completed'].includes(enrollmentStatus);
};

const StudentServicesPortal = () => {
    const { user, profile } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const view = searchParams.get('view') || 'semester-registration';
    const registeredOnly = view === 'registered-units';
    const simpleRegistration = view === 'semester-registration';
    const focusedRegistration = simpleRegistration || view === 'register-units';
    const [studentData, setStudentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [enrollment, setEnrollment] = useState({ context:null, semesterEnrollment:null, availableUnits:[], registrations:[] });
    const [enrollmentLoading, setEnrollmentLoading] = useState(false);
    const [notice, setNotice] = useState(null);
    const [selectedUnits, setSelectedUnits] = useState([]);
    const [letterLoading, setLetterLoading] = useState(false);

    useEffect(() => {
        if (profile?.id) {
            fetchStudentData(profile.id);
        }
    }, [profile]);

    const fetchStudentData = async (id) => {
        try {
            const data = await studentService.getStudentById(id);
            setStudentData(data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const loadEnrollment = async student => {
        if (!student?.profile_id || !student?.program_id) return;
        setEnrollmentLoading(true);
        try {
            const contextResult = await enrollmentService.getActiveEnrollmentContext();
            if (!contextResult.success) throw new Error(contextResult.error);
            const context = contextResult.data;
            if (!context.academicYear || !context.semester) {
                setEnrollment({ context, semesterEnrollment:null, availableUnits:[], registrations:[] });
                return;
            }
            const progression = await enrollmentService.syncStudentModuleProgression(student.profile_id);
            if (!progression.success) console.warn('Module progression sync was skipped:', progression.error);

            const semesterEnrollment = await enrollmentService.getSemesterEnrollment(
                student.profile_id,
                context.academicYear.id,
                context.semester.id
            );
            // Prefer the module stored on the current semester enrollment. It
            // is updated by the progression RPC and prevents a stale student
            // profile value from filtering the new semester back to Module 1.
            // The progression RPC is authoritative after a result release.
            // Keep the largest known value so an older semester-enrollment
            // row cannot make a student appear stuck in the previous module.
            const effectiveModule = Math.max(
                Number(progression.data?.current_module || 0),
                Number(semesterEnrollment?.module_number || 0),
                Number(student.current_module || student.starting_module || 1),
                1
            );
            if (progression.success && Number(progression.data?.current_module) > 0 &&
                Number(progression.data.current_module) !== Number(student.current_module || student.starting_module || 1)) {
                setStudentData(previous => previous ? { ...previous, current_module: Number(progression.data.current_module) } : previous);
            }
            const [unitResult, registrationResult] = await Promise.all([
                enrollmentService.getAvailableUnits(student.program_id, student.academic_level, context.semester.id, effectiveModule),
                enrollmentService.getStudentEnrollments(student.profile_id, context.academicYear.id, context.semester.id)
            ]);
            if (!unitResult.success) throw new Error(unitResult.error);
            if (!registrationResult.success) throw new Error(registrationResult.error);
            setEnrollment({
                context,
                semesterEnrollment,
                availableUnits: unitResult.data,
                registrations: (registrationResult.data || []).filter(isVisibleUnitRegistration),
            });
        } catch (error) { setNotice({ type:'error', message:error.message || 'Unable to load unit registration.' }); }
        finally { setEnrollmentLoading(false); }
    };

    useEffect(() => { if (studentData) loadEnrollment(studentData); }, [studentData]);

    const reportSemester = async () => {
        try {
            setNotice(null);
            const result = await enrollmentService.reportForSemester(studentData.profile_id, enrollment.context.academicYear.id, enrollment.context.semester.id);
            if (!result.success) throw new Error(result.error);
            setNotice({ type:'success', message:'Semester registration completed successfully.' });
            await loadEnrollment(studentData);
        } catch (error) { setNotice({ type:'error', message:error.message }); }
    };

    const registerUnit = async unit => {
        try {
            setNotice(null);
            const result = await enrollmentService.enrollStudentInUnit({ studentId:studentData.profile_id, academicYearId:enrollment.context.academicYear.id, semesterId:enrollment.context.semester.id, unitId:unit.id });
            if (!result.success) throw new Error(result.error);
            setNotice({ type:'success', message:`${unit.code} registered successfully.` });
            await loadEnrollment(studentData);
        } catch (error) { setNotice({ type:'error', message:error.message }); }
    };

    const registerSelected = async () => { for (const unit of enrollment.availableUnits.filter(item => selectedUnits.includes(item.id))) await registerUnit(unit); setSelectedUnits([]); };
    const completeRegistration = async () => {
        try {
            const result = await enrollmentService.completeUnitRegistration(enrollment.semesterEnrollment.id);
            if (!result.success) throw new Error(result.error);
            setNotice({ type:'success', message:'Unit registration completed and billed automatically.' });
            await loadEnrollment(studentData);
        } catch (error) { setNotice({ type:'error', message:error.message }); }
    };

    const downloadAdmissionLetter = async () => {
        setLetterLoading(true);
        try {
            let letterData = null;

            if (studentData.source_application_id) {
                // Primary path: fetch from the linked admission application
                const { data, error } = await admissionService.getAdmissionLetterData(studentData.source_application_id);
                if (!error && data) {
                    letterData = data;
                }
            }
            
            if (!letterData) {
                // Fallback: build letter data directly from student record + institution settings
                const { data: settings } = await supabase
                    .from('institution_settings')
                    .select('name, motto, logo_url, phone, email, website, postal_address, address, locale, registrar_name, registrar_title, registrar_signature_url, admissions_office_name, admissions_email, admissions_phone, official_reference_prefix, admission_letter_footer')
                    .limit(1)
                    .maybeSingle();
                    
                const s = settings || {};
                letterData = {
                    application_number: studentData.admission_number,
                    registration_number: studentData.admission_number,
                    applicant_name: [studentData.first_name, studentData.middle_name, studentData.last_name].filter(Boolean).join(' '),
                    program_name: studentData.program?.name,
                    applied_level: studentData.academic_level,
                    intake_name: studentData.intake_code,
                    academic_year: '',
                    reporting_date: null,
                    offered_at: studentData.created_at,
                    student_email: studentData.institutional_email || studentData.email,
                    institution_name: s.name,
                    institution_motto: s.motto,
                    institution_logo_url: s.logo_url,
                    institution_phone: s.phone,
                    institution_email: s.email,
                    institution_website: s.website,
                    institution_postal_address: s.postal_address,
                    institution_address: s.address,
                    institution_locale: s.locale,
                    registrar_name: s.registrar_name,
                    registrar_title: s.registrar_title,
                    registrar_signature_url: s.registrar_signature_url,
                    admissions_office_name: s.admissions_office_name,
                    admissions_email: s.admissions_email,
                    admissions_phone: s.admissions_phone,
                    letter_reference_prefix: s.official_reference_prefix,
                    admission_letter_footer: s.admission_letter_footer,
                };
            }

            await generateAdmissionLetter(letterData);
        } catch (error) {
            setNotice({ type: 'error', message: error.message || 'Could not generate admission letter.' });
        } finally {
            setLetterLoading(false);
        }
    };

    if (loading) return <StudentLayout><div className="p-8 text-center text-gray-500">Loading your profile...</div></StudentLayout>;

    if (!studentData) {
        return (
            <StudentLayout><div className="p-8 text-center">
                <Card className="max-w-lg mx-auto p-6">
                    <h2 className="text-xl font-bold text-red-600 mb-2">Access Restricted</h2>
                    <p className="text-gray-600 mb-4">
                        We could not find your student record. Please contact the registrar if you believe this is an error.
                    </p>
                    <p className="text-xs text-gray-400">Profile ID: {profile?.id}</p>
                </Card>
            </div></StudentLayout>
        );
    }

    return (
        <StudentLayout student={studentData}><div className="space-y-4">
            {/* Header */}
            {!registeredOnly && !focusedRegistration && <div className="rounded-xl bg-primary px-5 py-4 text-primary-foreground shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h1 className="text-xl font-bold">Welcome, {studentData.first_name}</h1>
                        <p className="mt-1 text-sm text-primary-foreground/75">
                            {studentData.program?.name} • Intake {studentData.intake_code}
                        </p>
                    </div>
                    <div className="rounded-lg border border-white/15 bg-white/10 px-3 py-2 text-right"><span className="block text-[0.625rem] font-semibold uppercase tracking-wider text-primary-foreground/70">Current module</span><strong className="text-lg">Module {studentData.current_module}</strong></div>
                </div>
            </div>}

            {view === 'semester-registration' && enrollment.semesterEnrollment && <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">Semester reporting is complete for the selected teaching period.</div>}
            {view === 'register-units' && enrollment.semesterEnrollment?.status === 'active' && enrollment.registrations.length > 0 && <div className="mb-3 flex justify-end"><Button onClick={completeRegistration}>Complete &amp; bill registration</Button></div>}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                <Card className="md:col-span-3">
                    <CardHeader><CardTitle>{registeredOnly ? 'Registered Units' : simpleRegistration ? 'Semester Registration' : 'Register Units'}</CardTitle></CardHeader>
                    <CardContent className={view === 'register-units' ? '[&_th:nth-child(4)]:hidden [&_td:nth-child(4)]:hidden' : ''}>
                        {notice && <div className={`mb-4 rounded-lg border p-3 text-sm ${notice.type === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'}`}>{notice.message}</div>}
                        {enrollmentLoading ? <p className="text-sm text-gray-500">Loading semester details…</p> : !enrollment.context?.semester ? <div className="rounded-lg border border-dashed p-6 text-center text-sm text-gray-500">No active academic year and teaching period has been configured.</div> : <>
                            <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border bg-slate-50 px-4 py-3 text-sm">
                                <div><span className="mr-2 text-xs font-semibold uppercase text-gray-500">Year</span>{enrollment.context.academicYear.name}</div>
                                <div><span className="mr-2 text-xs font-semibold uppercase text-gray-500">Period</span>{enrollment.context.semester.name}</div>
                                <div><span className="mr-2 text-xs font-semibold uppercase text-gray-500">Structure</span>Level {studentData.academic_level} · Module {studentData.current_module || studentData.starting_module || 1}</div>
                            </div>
                            {simpleRegistration ? (!enrollment.semesterEnrollment ? <div className="rounded-lg border border-blue-200 bg-blue-50 p-5"><h3 className="font-semibold">Semester reporting required</h3><p className="mt-1 text-sm text-gray-600">Report for the selected teaching period to activate your semester registration.</p><Button className="mt-4" onClick={reportSemester}>Report for semester</Button></div> : enrollment.semesterEnrollment.status === 'reported' ? <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">Your semester report is awaiting institutional activation.</div> : <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">Your semester registration is active for this teaching period.</div>) : !enrollment.semesterEnrollment ? <div className="rounded-lg border border-blue-200 bg-blue-50 p-5"><h3 className="font-semibold">Semester reporting required</h3><p className="mt-1 text-sm text-gray-600">Report for this semester before registering units.</p><Button className="mt-4" onClick={reportSemester}>Report for semester</Button></div> : <>
                                {enrollment.semesterEnrollment.status === 'reported' && <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">Your semester report is awaiting institutional activation. Unit registration is disabled.</div>}
                                {['registration_completed','billed'].includes(enrollment.semesterEnrollment.status) && <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">Registration has been completed{enrollment.semesterEnrollment.status === 'billed' ? ' and charges have been posted' : ''}. Contact your department if a correction is required.</div>}
                                <div className="overflow-x-auto rounded-lg border"><table className="w-full text-left text-sm"><thead className="bg-slate-50 text-xs uppercase text-gray-500"><tr><th className="px-4 py-3">Unit</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Credits</th>{!registeredOnly && <th className="px-4 py-3">Capacity</th>}<th className="px-4 py-3 text-right">Action</th></tr></thead><tbody>{(registeredOnly ? enrollment.availableUnits.filter(unit => enrollment.registrations.some(row => String(row.unit_id) === String(unit.id))) : enrollment.availableUnits).length ? (registeredOnly ? enrollment.availableUnits.filter(unit => enrollment.registrations.some(row => String(row.unit_id) === String(unit.id))) : enrollment.availableUnits).map(unit => { const registered=enrollment.registrations.find(row=>String(row.unit_id)===String(unit.id)); const canRegister=!registeredOnly && enrollment.semesterEnrollment.status==='active'&&!registered; return <tr key={unit.id} className="border-t"><td className="px-4 py-3"><p className="font-semibold">{unit.code}</p><p className="text-gray-600">{unit.name}</p></td><td className="px-4 py-3 capitalize">{unit.unit_type || unit.classification || '—'}</td><td className="px-4 py-3">{unit.credit_hours ?? '—'}</td>{!registeredOnly && <td className="px-4 py-3">{unit.max_capacity ? `${unit.current_enrollment}/${unit.max_capacity}` : 'Open'}</td>}<td className="px-4 py-3 text-right">{registered ? <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold capitalize text-green-700">{registered.enrollment_status}</span> : <Button size="sm" disabled={!canRegister} onClick={()=>registerUnit(unit)}>Register</Button>}</td></tr> }) : <tr><td colSpan={registeredOnly ? 4 : 5} className="px-4 py-10 text-center text-gray-500">{registeredOnly ? 'No units have been registered for this semester.' : 'No active units are configured for this programme, level and current module.'}</td></tr>}</tbody></table></div>
                            </>}
                        </>}
                    </CardContent>
                </Card>

                {/* Academic Profile */}
                {!registeredOnly && !focusedRegistration && <Card className="md:col-span-2">
                    <CardHeader>
                        <CardTitle>Academic Profile</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 gap-y-4 gap-x-8">
                            <div>
                                <label className="text-xs text-gray-500 uppercase font-semibold">Admission Number</label>
                                <p className="font-medium text-lg">{studentData.admission_number}</p>
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 uppercase font-semibold">Status</label>
                                <p className={`font-medium text-lg ${studentData.status === 'Active' ? 'text-green-600' : 'text-yellow-600'}`}>
                                    {studentData.status}
                                </p>
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 uppercase font-semibold">Starting Module</label>
                                <p className="font-medium text-lg">Module {studentData.starting_module}</p>
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 uppercase font-semibold">Program Code</label>
                                <p className="font-medium text-lg">{studentData.program?.code}</p>
                            </div>
                        </div>

                        {/* Documents */}
                        <div className="mt-8 pt-6 border-t">
                            <h3 className="font-semibold mb-4 flex items-center">
                                <AppIcon name="file" className="w-4 h-4 mr-2" />
                                My Documents
                            </h3>

                            {/* Admission Letter */}
                            <div className="mb-4 flex items-center justify-between rounded-lg border bg-blue-50 border-blue-200 px-4 py-3">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600">
                                        <FileText className="h-4 w-4 text-white" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-blue-900">Admission Letter</p>
                                        <p className="text-xs text-blue-600">Reg. No: {studentData.admission_number}</p>
                                    </div>
                                </div>
                                <Button
                                    size="sm"
                                    onClick={downloadAdmissionLetter}
                                    disabled={letterLoading}
                                    className="gap-2"
                                >
                                    <Download className="h-4 w-4" />
                                    {letterLoading ? 'Generating…' : 'Download'}
                                </Button>
                            </div>

                            {studentData.documents && studentData.documents.length > 0 ? (
                                <ul className="space-y-2">
                                    {studentData.documents.map(doc => (
                                        <li key={doc.id} className="flex justify-between items-center bg-gray-50 p-3 rounded">
                                            <span className="text-sm font-medium">{doc.document_name}</span>
                                            <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-xs hover:underline">Download</a>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-sm text-gray-500 italic">No other approved documents on file.</p>
                            )}
                        </div>
                    </CardContent>
                </Card>}

                {/* Quick Actions */}
                {!registeredOnly && !focusedRegistration && <Card>
                    <CardHeader>
                        <CardTitle>Account Actions</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <Button className="w-full justify-start" variant="outline" onClick={() => navigate('/change-password')}>
                            <AppIcon name="lock" className="mr-2 h-4 w-4" />
                            Change Password
                        </Button>
                        <Button className="w-full justify-start" variant="outline">
                            <AppIcon name="user" className="mr-2 h-4 w-4" />
                            View Full Profile
                        </Button>
                        <Button className="w-full justify-start" variant="outline">
                            <AppIcon name="help-circle" className="mr-2 h-4 w-4" />
                            Help & Support
                        </Button>
                    </CardContent>
                </Card>}
            </div>
        </div></StudentLayout>
    );
};

export default StudentServicesPortal;
